import { useState, useEffect, useCallback } from "react";
import api from "../services/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  UserPlus, Trash2, UserCheck, UserX, ShieldCheck, Eye, EyeOff, X
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

function CreateUserModal({ onClose, onCreated }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post("/auth/users", { email, name, password });
      toast.success(`${res.data.name} kullanıcısı oluşturuldu`);
      onCreated(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kullanıcı oluşturulamadı");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      data-testid="create-user-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
              <UserPlus size={15} className="text-blue-600" />
            </div>
            <h2 className="text-base font-semibold text-slate-900">Yeni Admin Kullanıcı</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Ad Soyad</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ad Soyad"
              data-testid="new-user-name"
              required
              className="h-9 text-sm border-slate-200"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">E-posta</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@ornek.com"
              data-testid="new-user-email"
              required
              className="h-9 text-sm border-slate-200"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Şifre</label>
            <div className="relative">
              <Input
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="En az 6 karakter"
                data-testid="new-user-password"
                required
                className="h-9 text-sm pr-9 border-slate-200"
              />
              <button type="button" onClick={() => setShowPw(v => !v)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>

          <div className="flex gap-2 pt-1">
            <Button type="submit" disabled={saving} data-testid="create-user-submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white h-9 text-sm">
              {saving ? "Oluşturuluyor..." : "Kullanıcı Oluştur"}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}
              className="h-9 text-sm border-slate-200">
              İptal
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminUsers() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/auth/users")
      .then((r) => setUsers(r.data))
      .catch(() => toast.error("Kullanıcılar yüklenemedi"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreated = (newUser) => {
    setUsers((prev) => [...prev, newUser]);
  };

  const handleDelete = async (u) => {
    setDeletingId(u.id);
    try {
      await api.delete(`/auth/users/${u.id}`);
      toast.success(`${u.name} kullanıcısı silindi`);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      setConfirmDeleteId(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Silinemedi");
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggle = async (u) => {
    setTogglingId(u.id);
    try {
      const res = await api.patch(`/auth/users/${u.id}/toggle-active`);
      setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, is_active: res.data.is_active } : x));
      toast.success(res.data.is_active ? `${u.name} aktif edildi` : `${u.name} devre dışı bırakıldı`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Güncellenemedi");
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">
            Admin Kullanıcılar
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Yönetim paneline erişimi olan hesapları yönetin
          </p>
        </div>
        <Button
          onClick={() => setShowCreate(true)}
          data-testid="add-user-btn"
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm h-9 gap-2"
        >
          <UserPlus size={14} />
          Yeni Kullanıcı
        </Button>
      </div>

      {/* Users list */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="users-list">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
          <ShieldCheck size={14} className="text-slate-400" />
          <span className="text-sm font-semibold text-slate-700">
            {loading ? "Yükleniyor..." : `${users.length} Admin Kullanıcı`}
          </span>
        </div>

        {loading ? (
          <div className="p-6 space-y-3">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="h-14 bg-slate-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400">
            Hiç admin kullanıcı bulunamadı
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {users.map((u) => {
              const isSelf = u.id === currentUser?.id;
              return (
                <div key={u.id} data-testid={`user-row-${u.id}`}
                  className="flex items-center gap-4 px-5 py-4">
                  {/* Avatar */}
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                    u.is_active !== false ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-400"
                  }`}>
                    {(u.name || "?")[0].toUpperCase()}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-slate-800 truncate">{u.name}</span>
                      {isSelf && (
                        <span className="text-xs bg-blue-50 text-blue-600 border border-blue-200 px-1.5 py-0.5 rounded font-medium">
                          Siz
                        </span>
                      )}
                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                        u.is_active !== false
                          ? "bg-green-50 text-green-700 border border-green-200"
                          : "bg-slate-100 text-slate-500 border border-slate-200"
                      }`}>
                        {u.is_active !== false ? "Aktif" : "Pasif"}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5 truncate">{u.email}</p>
                    <p className="text-xs text-slate-300 mt-0.5">
                      Oluşturma: {u.created_at ? u.created_at.slice(0, 10) : "—"}
                    </p>
                  </div>

                  {/* Actions */}
                  {!isSelf && (
                    <div className="flex items-center gap-2 shrink-0">
                      {confirmDeleteId === u.id ? (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-500">Silinsin mi?</span>
                          <button
                            data-testid={`confirm-delete-btn-${u.id}`}
                            onClick={() => handleDelete(u)}
                            disabled={deletingId === u.id}
                            className="px-2.5 py-1 text-xs bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors disabled:opacity-50"
                          >
                            {deletingId === u.id ? "..." : "Evet"}
                          </button>
                          <button
                            onClick={() => setConfirmDeleteId(null)}
                            className="px-2.5 py-1 text-xs border border-slate-200 text-slate-500 hover:bg-slate-50 rounded-lg transition-colors"
                          >
                            İptal
                          </button>
                        </div>
                      ) : (
                        <>
                          <button
                            data-testid={`toggle-btn-${u.id}`}
                            onClick={() => handleToggle(u)}
                            disabled={togglingId === u.id}
                            title={u.is_active !== false ? "Devre Dışı Bırak" : "Aktif Et"}
                            className={`w-8 h-8 rounded-lg border flex items-center justify-center transition-colors disabled:opacity-50 ${
                              u.is_active !== false
                                ? "border-slate-200 text-slate-400 hover:border-amber-300 hover:text-amber-600 hover:bg-amber-50"
                                : "border-slate-200 text-slate-400 hover:border-green-300 hover:text-green-600 hover:bg-green-50"
                            }`}
                          >
                            {togglingId === u.id ? (
                              <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                            ) : u.is_active !== false ? (
                              <UserX size={14} />
                            ) : (
                              <UserCheck size={14} />
                            )}
                          </button>
                          <button
                            data-testid={`delete-btn-${u.id}`}
                            onClick={() => setConfirmDeleteId(u.id)}
                            title="Kullanıcıyı Sil"
                            className="w-8 h-8 rounded-lg border border-slate-200 text-slate-400 hover:border-red-300 hover:text-red-500 hover:bg-red-50 flex items-center justify-center transition-colors"
                          >
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Info note */}
      <div className="flex items-start gap-2 text-xs text-slate-400">
        <ShieldCheck size={13} className="shrink-0 mt-0.5" />
        <span>
          Tüm admin kullanıcılar yönetim paneline tam erişime sahiptir. 
          Kendi hesabınızı silemez veya devre dışı bırakamazsınız.
        </span>
      </div>

      {showCreate && (
        <CreateUserModal onClose={() => setShowCreate(false)} onCreated={handleCreated} />
      )}
    </div>
  );
}
